/*
 * ErrorBoundary
 * ─────────────────────────────────────────────────────────────────────
 * Top-of-tree safety net.  Catches any render / lifecycle crash and
 * renders an inline diagnostic UI INSTEAD of letting the error bubble
 * up to Emergent's preview-iframe instrumentation (which then tries to
 * window.postMessage the Error and fails with
 * "Request object could not be cloned" — masking the real crash).
 *
 * Shows:
 *   • the real error message + stack  (so we can see what actually broke)
 *   • the React component stack       (so we can pinpoint which component)
 *   • the current URL path            (so we can reproduce)
 *   • a "Copy details" button         (quick hand-off to the dev)
 *   • a "Try again" button            (resets the boundary state)
 *   • a "Go back" button              (returns to previous route)
 *
 * The boundary NEVER calls window.postMessage so it can't itself trip
 * the clone error.
 */
import React from 'react';
import * as Sentry from '@sentry/react';
import { AlertTriangle, RefreshCw, ArrowLeft, Copy, CheckCircle2 } from 'lucide-react';
import { isClientFacingPath } from '../lib/publicRoutes';
import ClientLoadFailure from './ClientLoadFailure';
import {
  isStaleBuildError,
  handleBoundaryChunkError,
} from '../lib/chunkRecovery';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null, copied: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Report the real crash to Sentry (no-op if Sentry isn't initialized).
    try {
      // A dead / undelivered code chunk is NOT a component bug — funnel it into
      // the single chunk-failure decision (which reloads once or shows the
      // shared banner). Everything else is a genuine crash to capture.
      if (!handleBoundaryChunkError(error)) {
        Sentry.captureException(error, { contexts: { react: { componentStack: info?.componentStack } } });
      }
    } catch {
      /* never let reporting crash the boundary */
    }
    // Only console.error — never postMessage — so the Emergent preview
    // iframe can't re-throw during logging.
    try {
      // eslint-disable-next-line no-console
      console.error(
        '[ErrorBoundary]',
        error,
        '\ncomponentStack:',
        info?.componentStack,
      );
    } catch {
      /* swallow — logging must never crash the boundary */
    }
    this.setState({ info });
  }

  handleReset = () => {
    this.setState({ error: null, info: null, copied: false });
  };

  handleBack = () => {
    this.setState({ error: null, info: null, copied: false });
    if (typeof window !== 'undefined' && window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = '/';
    }
  };

  handleCopy = async () => {
    const { error, info } = this.state;
    const payload = [
      `URL: ${typeof window !== 'undefined' ? window.location.href : ''}`,
      `Time: ${new Date().toISOString()}`,
      `User-Agent: ${typeof navigator !== 'undefined' ? navigator.userAgent : ''}`,
      `Error: ${error?.name || 'Error'}: ${error?.message || String(error)}`,
      `Stack:\n${error?.stack || '(no stack)'}`,
      `Component stack:${info?.componentStack || '\n(no component stack)'}`,
    ].join('\n\n');

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(payload);
      } else {
        // Fallback for environments without clipboard API
        const ta = document.createElement('textarea');
        ta.value = payload;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    } catch {
      /* noop — clipboard blocked in iframe is common, user can still read the details */
    }
  };

  render() {
    if (!this.state.error) return this.props.children;

    const { error, info, copied } = this.state;
    // A deploy landed while this tab was open: the code file this page is
    // asking for no longer exists / failed to load. The single chunk-failure
    // handler already decided (reload once, or raise the shared banner) from
    // componentDidCatch — so the boundary must NOT render its own dead screen.
    // A client keeps their branded fallback; everyone else falls through to the
    // banner with nothing blocking the page.
    if (isStaleBuildError(error)) {
      if (typeof window !== 'undefined' && isClientFacingPath(window.location.pathname)) {
        return <ClientLoadFailure />;
      }
      return null;
    }
    const isCloneError = /could not be cloned|structured clone/i.test(error?.message || '');

    return (
      <div
        data-testid="error-boundary-fallback"
        style={{
          minHeight: '100vh',
          background:
            'linear-gradient(180deg, #0a0f1f 0%, #050814 100%)',
          color: '#E6E8EE',
          padding: '48px 24px',
          fontFamily: 'Inter, -apple-system, system-ui, sans-serif',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
        }}
      >
        <div style={{ maxWidth: 860, width: '100%' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 14,
              padding: 24,
              borderRadius: 14,
              border: '1px solid rgba(251,146,60,0.3)',
              background: 'rgba(251,146,60,0.05)',
              marginBottom: 20,
            }}
          >
            <AlertTriangle
              style={{ width: 22, height: 22, color: '#FB923C', flexShrink: 0, marginTop: 2 }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1
                style={{
                  margin: 0,
                  fontSize: 18,
                  fontWeight: 700,
                  color: '#fff',
                }}
              >
                Something broke while rendering this page
              </h1>
              <p
                style={{
                  margin: '6px 0 0',
                  fontSize: 13,
                  color: 'rgba(255,255,255,0.6)',
                  lineHeight: 1.55,
                }}
              >
                The error below is the real crash — not the generic preview
                overlay. Copy the details and share them with the engineer
                so the underlying component can be fixed.
              </p>
              {isCloneError && (
                <p
                  style={{
                    margin: '10px 0 0',
                    padding: '8px 12px',
                    borderRadius: 8,
                    background: 'rgba(212,175,55,0.08)',
                    border: '1px solid rgba(212,175,55,0.25)',
                    fontSize: 12,
                    color: '#D4AF37',
                  }}
                >
                  Note: this error mentions a structured-clone issue. That
                  usually comes from the preview iframe's network logger —
                  the underlying app crash is what produced it. Look at the
                  component stack below to find the real culprit.
                </p>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
            <Btn onClick={this.handleReset} icon={RefreshCw} data-testid="error-boundary-retry-btn">
              Try again
            </Btn>
            <Btn onClick={this.handleBack} icon={ArrowLeft} data-testid="error-boundary-back-btn">
              Go back
            </Btn>
            <Btn
              onClick={this.handleCopy}
              icon={copied ? CheckCircle2 : Copy}
              gold
              data-testid="error-boundary-copy-btn"
            >
              {copied ? 'Copied' : 'Copy full details'}
            </Btn>
          </div>

          {/* Meta */}
          <DetailBlock label="URL" data-testid="error-boundary-url">
            {typeof window !== 'undefined' ? window.location.href : ''}
          </DetailBlock>

          <DetailBlock label="Error" data-testid="error-boundary-message" tone="red">
            {(error?.name || 'Error') + ': ' + (error?.message || String(error))}
          </DetailBlock>

          <DetailBlock label="Stack trace" data-testid="error-boundary-stack" mono>
            {error?.stack || '(no stack)'}
          </DetailBlock>

          <DetailBlock label="Component stack" data-testid="error-boundary-component-stack" mono>
            {info?.componentStack || '(no component stack)'}
          </DetailBlock>
        </div>
      </div>
    );
  }
}

const Btn = ({ onClick, icon: Icon, gold, children, ...rest }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '10px 16px',
      borderRadius: 8,
      border: gold ? 'none' : '1px solid rgba(255,255,255,0.12)',
      background: gold
        ? 'linear-gradient(135deg,#E6C86A 0%,#D4AF37 50%,#B88B1A 100%)'
        : 'rgba(255,255,255,0.04)',
      color: gold ? '#0F0F1A' : '#E6E8EE',
      fontSize: 13,
      fontWeight: 600,
      cursor: 'pointer',
      fontFamily: 'inherit',
    }}
    {...rest}
  >
    {Icon && <Icon style={{ width: 14, height: 14 }} />}
    {children}
  </button>
);

const DetailBlock = ({ label, children, mono, tone, ...rest }) => (
  <div
    style={{
      marginBottom: 12,
      border: '1px solid rgba(255,255,255,0.06)',
      borderRadius: 10,
      padding: '12px 16px',
      background: 'rgba(255,255,255,0.02)',
    }}
    {...rest}
  >
    <p
      style={{
        margin: 0,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.12,
        textTransform: 'uppercase',
        color: 'rgba(255,255,255,0.45)',
      }}
    >
      {label}
    </p>
    <pre
      style={{
        margin: '6px 0 0',
        fontSize: mono ? 11 : 13,
        lineHeight: 1.55,
        fontFamily: mono
          ? 'ui-monospace, "SF Mono", Menlo, Consolas, monospace'
          : 'inherit',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        color: tone === 'red' ? '#FCA5A5' : 'rgba(255,255,255,0.85)',
        maxHeight: 340,
        overflow: 'auto',
      }}
    >
      {children}
    </pre>
  </div>
);
